import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Banner, Heading, Spinner } from "@panelgenie/shared-ui";
import { TestCasesList } from "../../components/TestCasesList.js";

/**
 * TC-7 — the AI Scan Test-Case Corpus list. Reuses the invocation-list
 * envelope (query → loading / error / table); rows link to the case's
 * detail page. The corpus is small and curated, so there's no paging or
 * filter bar — every case is shown, server-ordered.
 */
export const Route = createFileRoute("/test-cases")({
  component: TestCasesListPage,
});

function TestCasesListPage() {
  const { trpc } = Route.useRouteContext();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ["test-cases", "list"],
    queryFn: () => trpc.support.listTestCases.query(),
  });

  const goToDetail = (id: string) => {
    void navigate({ to: "/test-cases/$id", params: { id } });
  };

  return (
    <div className="mx-auto flex h-full w-full max-w-6xl flex-col gap-6 overflow-y-auto px-4 py-6">
      <div>
        <Heading level="h1">Test cases</Heading>
        <p className="mt-1 text-base text-mute">
          The AI panel-scan regression corpus — curated baselines you can
          re-run to verify a process change against an immutable golden.
        </p>
      </div>

      {query.isError ? (
        <Banner variant="danger" title="Couldn't load test cases">
          {query.error instanceof Error
            ? query.error.message
            : "Request failed. Staff access is required for this surface."}
        </Banner>
      ) : query.isPending ? (
        <div className="flex items-center gap-3 text-mute">
          <Spinner size="md" />
          <span>Loading test cases…</span>
        </div>
      ) : (
        <TestCasesList items={query.data.items} onSelect={goToDetail} />
      )}
    </div>
  );
}
